/**
 * Module access control utilities.
 *
 * Role hierarchy (mirrors backend AuthenticatedUser):
 *   SUPER_ADMIN  — user.platformRole === "super_admin" (or legacy user.role === "admin")
 *   ORG_ADMIN    — user.isAdmin === 1 (set by backend for org admins + super admins)
 *   ORG_HR       — user.isHr === true  (set by backend for org HR members)
 *   ORG_EDITOR   — user.role === "editor" or user.org_role === "editor"
 *   MEMBER       — everyone else
 *
 * Available module keys: 'events', 'learning', 'kudos', 'people', 'workshops'
 */

export const AVAILABLE_MODULES = [
  { key: 'events', labelKey: 'modules.events' },
  { key: 'learning', labelKey: 'modules.learning' },
  { key: 'kudos', labelKey: 'modules.kudos' },
  { key: 'people', labelKey: 'modules.people' },
  { key: 'workshops', labelKey: 'modules.workshops' },
];

/** True when the user is a platform-level SUPER_ADMIN. */
export function isSuperAdmin(user): boolean {
  return user?.platformRole === 'super_admin' || user?.platform_role === 'super_admin';
}

/** True when the user is a SUPER_ADMIN or ORG_ADMIN of the active org. */
export function isAdmin(user): boolean {
  return !!user?.isAdmin;
}

/** True when the user has HR access in the active org. */
export function isHr(user): boolean {
  return !!user?.isHr;
}

/** True when the user has admin or HR access (can manage org content and members). */
export function isAdminOrHr(user): boolean {
  return isAdmin(user) || isHr(user);
}

/** A right that can be given to one person on top of their role (ADR-043). */
export type Permission = 'moderate_kudos' | 'approve_events' | 'view_analytics' | 'manage_champions' | 'review_contributions';

/** The rights given to this person; admins and HR hold them all through their role. */
export function grantedPermissions(user): string[] {
  return Array.isArray(user?.permissions) ? user.permissions : [];
}

/** True when the role brings this right (admins and HR) or it was given to this person. */
export function can(user, permission: Permission): boolean {
  return isAdminOrHr(user) || grantedPermissions(user).includes(permission);
}

/** Admin and HR, or anyone given a permission: they open the Admin Center (and see only their part). */
export function hasAdminAccess(user): boolean {
  return isAdminOrHr(user) || grantedPermissions(user).length > 0;
}

/** True when the user can manage content (admin, HR, or editor). */
export function canManageContent(user): boolean {
  if (!user) return false;
  return isAdminOrHr(user) || user.role === 'editor' || user.org_role === 'editor';
}

export function hasModule(user, moduleKey): boolean {
  if (!user) return false;
  // Admin and HR bypass all module checks
  if (isAdminOrHr(user)) return true;
  // Plain members default to all modules when no explicit access entries;
  // editors must have explicit module access and are excluded from this fallback
  if (user.status === 'active'
      && user.role !== 'editor' && user.org_role !== 'editor'
      && (!Array.isArray(user.moduleAccess) || user.moduleAccess.length === 0)) {
    return true;
  }
  return Array.isArray(user.moduleAccess) && user.moduleAccess.includes(moduleKey);
}

export function canCreateEvent(user): boolean {
  return !!user && hasModule(user, 'events');
}

export function canCreateCourse(user): boolean {
  return isAdminOrHr(user) && hasModule(user, 'learning');
}

export function canEditCourse(user): boolean {
  if (!user || !hasModule(user, 'learning')) return false;
  return canManageContent(user);
}
